import type { store as appStore } from './store';

type AppStore = typeof appStore;

/**
 * Lazy access to the app store, for modules that must NOT hold a module-scope
 * import of `store.ts` (DV-3013 / spec §3.8, closing the follow-up banked in
 * DV-3012's close-out §3).
 *
 * `store.ts`'s module body eagerly reads `someApi.reducerPath` inside its
 * `configureStore({ reducer: {...} })` object literal. So any import cycle that
 * passes THROUGH `store.ts` while it is mid-evaluation observes an api slice as
 * `undefined` — the cycle
 *   auth-token -> entity-scope-resolution -> entity-scope-api -> store
 *              -> an api slice -> api-base -> auth-token
 * failed exactly that way, in 27 unrelated test files, with
 * `TypeError: Cannot read properties of undefined (reading 'reducerPath')`.
 *
 * This module imports nothing from the app (the `import type` above is erased
 * at compile time and creates no runtime edge). `store.ts` pushes itself in
 * here at the end of its own evaluation, and the scope services pull from here
 * instead — so no module-scope edge into `store.ts` survives.
 */
interface StoreApi {
  resetAllApiState: () => void;
}

let registeredStore: AppStore | null = null;
let registeredApi: StoreApi | null = null;

export const registerStore = (store: AppStore, api: StoreApi): void => {
  registeredStore = store;
  registeredApi = api;
};

export const getStore = (): AppStore => {
  if (!registeredStore) {
    throw new Error(
      'get-store: the store has not registered yet. store.ts calls registerStore() ' +
        'at the end of its evaluation; a caller reaching this before then has ' +
        'introduced a module-scope edge that runs too early.',
    );
  }
  return registeredStore;
};

export const getResetAllApiState = (): (() => void) => {
  if (!registeredApi) {
    throw new Error('get-store: the store has not registered yet (see getStore).');
  }
  return registeredApi.resetAllApiState;
};

/** Test-only. Production code must never call this. */
export const __resetRegistryForTests = (): void => {
  registeredStore = null;
  registeredApi = null;
};
